import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import AltRetailerLogo from 'images/Old_Navy_Logo.png'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { ArrowUp, ArrowDown, ArrowUpDown, MapPin } from 'lucide-react'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'

const headerCells = [
	{ id: 'image', label: 'Image', sortable: false },
	{ id: 'assetName', label: 'Name', sortable: true },
	{ id: 'address', label: 'Address', sortable: true },
	{ id: 'city', label: 'City', sortable: true },
	{ id: 'stateProvince', label: 'State', sortable: true },
	{ id: 'postalCode', label: 'Postal Code', sortable: true },
	{ id: 'action', label: 'Add', sortable: false },
]

const PortfolioAddLocationsTable = ({
	locations,
	addLocation,
	recordCount,
}) => {
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState(null)

	const handleOpenMap = (e, loc) => {
		e.stopPropagation()
		setMapLocation(loc)
		setOpenMap(true)
	}

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj)
	}

	const sortedRecords = useMemo(() => {
		if (!orderBy) return locations
		return [...locations].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [locations, orderBy, order])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ArrowUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ArrowDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	return (
		<div>
			<div>
				<div>
					<Table>
						<TableHeader>
							<TableRow>
								{headerCells.map(cell => (
									<TableHead
										key={cell.id}
										className={cell.sortable ? 'cursor-pointer select-none' : ''}
										onClick={() => cell.sortable && handleSort(cell.id)}
									>
										<div className="flex items-center">
											{cell.label}
											{cell.sortable && <SortIcon columnId={cell.id} />}
										</div>
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{sortedRecords.map(location => (
								<TableRow key={location._id} className="h-7 hover:bg-[#fffbf2] cursor-pointer">
									<TableCell className="py-1 text-sm">
										<Avatar><AvatarImage src={location.image} /><AvatarFallback><img src={AltRetailerLogo} className="h-full w-full object-cover" /></AvatarFallback></Avatar>
									</TableCell>
									<TableCell className="py-1 text-sm">
										<Link to={`/locations/${location._id}`}>{location.assetName}</Link>
									</TableCell>
									<TableCell className="py-1 text-sm">
										<button
											type="button"
											onClick={e => handleOpenMap(e, location)}
											className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
										>
											<MapPin className="h-3 w-3" />
											{location.address}
										</button>
									</TableCell>
									<TableCell className="py-1 text-sm">{location.city}</TableCell>
									<TableCell className="py-1 text-sm">{location.stateProvince}</TableCell>
									<TableCell className="py-1 text-sm">{location.postalCode}</TableCell>
									<TableCell className="py-1 text-sm">
										<button
											type="button"
											className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors text-primary"
											onClick={() => addLocation(location._id)}
										>
											<Plus className="h-4 w-4" />
										</button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</div>

			{openMap && mapLocation && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					location={mapLocation}
				/>
			)}
		</div>
	)
}

export default PortfolioAddLocationsTable
